import { createContext, useContext, useEffect, useState } from "react";

const TRANSLATIONS = {
  en: {
    "nav.search": "Search",
    "nav.facilities": "Facilities",
    "nav.map": "Status Map",
    "nav.alert": "Traveler Alert",
    "nav.admin": "Admin",
    "nav.adminLogin": "Admin login",
    "nav.logout": "Logout",
    "nav.langToggle": "Español",

    "home.badge": "Public information · Federal & State systems",
    "home.title": "Find an inmate via official locators, verify their institution, and check visitation status before you travel.",
    "home.subtitle": "Enter a name once and we route you to the official Federal Bureau of Prisons and state Department of Corrections locators. Then check the facility's address, visiting rules, and live operational status here.",
    "home.locatorNote": "Inmate records come only from official government locators. Your search opens the Federal BOP locator and the right state DOC locator, with the name pre-filled where the official site supports it.",
    "home.firstName": "First name",
    "home.lastName": "Last name",
    "home.middleName": "Middle name / initial",
    "home.inmateId": "Inmate / Register ID",
    "home.jurisdiction": "Jurisdiction",
    "home.dob": "Date of birth (optional)",
    "home.optional": "Optional",
    "home.anyJurisdiction": "Any jurisdiction",
    "home.sameNameNote": "Two people can share the same name. Results list all possible matches — verify against the official record before assuming identity.",
    "home.submit": "Search official locators",
    "home.cardProfilesTitle": "Institution profiles",
    "home.cardProfilesBody": "Every match links to a facility page with the correctional institution address, phone, visiting days & hours, and official source links.",
    "home.cardStatusTitle": "Live status & lockdowns",
    "home.cardStatusBody": "7 verified operational statuses — from Normal to Lockdown and Visitation Suspended — each with source and timestamp.",
    "home.cardVerifyTitle": "Verify official record",
    "home.cardVerifyBody": "Inmate records always come straight from the government source: the BOP Inmate Locator or the state DOC locator.",
    "home.coverageTitle": "Jurisdictions covered",
    "home.coverageBody": "Prioritized official government sources. Additional states can be added by administrators.",
    "home.officialAvailable": "Official locator available",
    "home.officialUnavailable": "Official online source not currently available",
    "home.open": "Open",
    "stats.facilities": "Facilities tracked",
    "stats.federal": "Federal BOP institutions",
    "stats.jurisdictions": "Official locators linked",
    "stats.verified": "With verified status",
    "stats.flagged": "Currently flagged",
    "flagged.title": "Recently flagged facilities",
    "flagged.seeAll": "All traveler alerts →",
    "flagged.body": "Lockdowns, suspended or limited visitation, and incidents — most recent first.",
    "flagged.updated": "Updated",
    "flagged.empty": "No facilities are currently flagged with a lockdown or visitation restriction.",

    "common.backSearch": "← Back to search",
    "common.newSearch": "← New search",
    "common.loading": "Loading...",

    "results.title": "Search results",
    "results.searching": "Searching official records...",
    "results.matches_one": "possible match found",
    "results.matches_other": "possible matches found",
    "results.none": "No matching records found in this app's dataset.",
    "results.noneHelp": "Try broadening your search, or check the official locator for the specific state or federal system directly.",
    "results.refine": "← Refine search",
    "results.inmateId": "Inmate ID",
    "results.dob": "DOB",
    "results.custody": "Custody status",
    "results.system": "System",
    "results.projectedRelease": "Projected release",
    "results.notPublic": "Not publicly available",
    "results.parole": "Parole",
    "results.demo": "Demo record",
    "results.ciAddress": "Correctional Institution Address",
    "results.viewFacility": "View facility & visitation",
    "results.verifyOfficial": "Verify official record",
    "results.source": "Source",
    "results.lastChecked": "Last checked",

    "facility.beforeYouTravel": "BEFORE YOU TRAVEL",
    "facility.beforeTravelLine1": "Correctional facility conditions can change without notice.",
    "facility.beforeTravelLine2": "Confirm visitation directly with the institution before making a long trip.",
    "facility.callFacility": "Call",
    "facility.viewOfficialNotice": "View official notice",
    "facility.directions": "Directions",
    "facility.institutionInfo": "Institution information",
    "facility.phone": "Phone",
    "facility.systemType": "System type",
    "facility.facilityType": "Facility type",
    "facility.officialWebsite": "Official website",
    "facility.visitationPage": "Visitation page",
    "facility.currentStatus": "Current status",
    "facility.lastUpdated": "Last updated",
    "facility.statusSource": "Source",
    "facility.officialSource": "View official source",
    "facility.visitationStatus": "Visitation status",
    "facility.visitationHeader": "Visitation status",
    "facility.visitationDays": "Visiting days",
    "facility.visitationHours": "Visiting hours",
    "facility.visitationPhone": "Visitation phone",
    "facility.idRequirements": "ID requirements",
    "facility.visitationRules": "Visitation rules",
    "facility.criticalAnnouncement": "Check the official institution announcement before traveling.",
    "facility.contactId": "Government-issued photo ID required. Verify specific rules with facility.",
    "facility.verifyDirectly": "Verify directly",
    "facility.verifyDirectlyBody": "Before traveling, confirm this facility's current status directly with the institution. Phone and official website links are above.",
    "facility.openOfficialSource": "Open official source →",
    "facility.downloadPacket": "Download visitor packet (PDF)",

    "subscribe.title": "Get status alerts",
    "subscribe.subtitle": "We'll notify you when this facility's status changes.",
    "subscribe.emailPlaceholder": "your@email.com",
    "subscribe.phonePlaceholder": "+1 (optional, for SMS)",
    "subscribe.cta": "Alert me",
    "human.question": "What is {a} + {b}?",
    "human.loading": "Loading check...",
    "human.placeholder": "Your answer",
    "human.refresh": "New question",
    "human.failed": "Human verification failed. Please answer the check again.",
    "subscribe.success": "You're subscribed. Watch your inbox for status changes.",
    "subscribe.privacy": "Transactional alerts only. We never share your email and never ask for passwords by email.",
    "subscribe.smsOff": "SMS alerts available when phone added (SMS may be inactive until Twilio is configured by admin).",

    "map.title": "Nationwide institution status map",
    "map.subtitle": "Every tracked facility, colored by live status. Click a marker for details.",
    "map.legend": "Legend",

    "footer.notice": "Public information notice",
    "footer.noticeBody": "This portal aggregates publicly available records from the Federal Bureau of Prisons and state Departments of Corrections. Correctional facility conditions can change without notice.",
    "footer.noticeStrong": "Confirm visitation directly with the institution before making a long trip.",
    "footer.data": "Data policy",
    "footer.dataBody": "Facility addresses shown here are Correctional Institution Addresses only, not personal addresses. All results include a link to the official government record.",
    "footer.emergency": "Emergency",
    "footer.emergencyBody": "If you have an emergency involving an incarcerated person, contact the facility directly using the official phone number listed on the facility profile page.",
    "footer.copyright": "US Prisoner & Institution Status Portal · Non-affiliated public information tool",

    "status.NORMAL": "NORMAL OPERATIONS",
    "status.RESTRICTED": "RESTRICTED OPERATIONS",
    "status.LIMITED_VISITATION": "LIMITED VISITATION",
    "status.LOCKDOWN": "LOCKDOWN",
    "status.VISITATION_SUSPENDED": "VISITATION SUSPENDED",
    "status.EMERGENCY": "EMERGENCY / INCIDENT",
    "status.UNKNOWN": "Status pending verification",
    "status.unverified": "Status pending verification",
  },
  es: {
    "nav.search": "Buscar",
    "nav.facilities": "Instituciones",
    "nav.map": "Mapa de estado",
    "nav.alert": "Alerta para visitantes",
    "nav.admin": "Administración",
    "nav.adminLogin": "Acceso administrador",
    "nav.logout": "Cerrar sesión",
    "nav.langToggle": "English",

    "home.badge": "Información pública · Sistemas federal y estatales",
    "home.title": "Encuentre a un recluso mediante los localizadores oficiales, verifique su institución y confirme el estado de visitas antes de viajar.",
    "home.subtitle": "Ingrese un nombre y le dirigimos a los localizadores oficiales de la Oficina Federal de Prisiones (BOP) y de los Departamentos Estatales de Correcciones. Luego consulte aquí la dirección, las reglas de visita y el estado operativo de la institución.",
    "home.locatorNote": "Los registros de reclusos provienen solo de los localizadores oficiales del gobierno. Su búsqueda abre el localizador del BOP y el localizador estatal correspondiente, con el nombre ya ingresado cuando el sitio oficial lo permite.",
    "home.firstName": "Nombre",
    "home.lastName": "Apellido",
    "home.middleName": "Segundo nombre / inicial",
    "home.inmateId": "ID de recluso / registro",
    "home.jurisdiction": "Jurisdicción",
    "home.dob": "Fecha de nacimiento (opcional)",
    "home.optional": "Opcional",
    "home.anyJurisdiction": "Cualquier jurisdicción",
    "home.sameNameNote": "Dos personas pueden compartir el mismo nombre. Los resultados listan todas las posibles coincidencias — verifique con el registro oficial antes de suponer la identidad.",
    "home.submit": "Buscar en localizadores oficiales",
    "home.cardProfilesTitle": "Perfiles de instituciones",
    "home.cardProfilesBody": "Cada coincidencia enlaza con una ficha de institución con dirección correccional, teléfono, días y horarios de visita y enlaces a fuentes oficiales.",
    "home.cardStatusTitle": "Estado y encierros en vivo",
    "home.cardStatusBody": "7 estados operativos verificados — desde Normal hasta Encierro y Visitas Suspendidas — cada uno con fuente y marca de tiempo.",
    "home.cardVerifyTitle": "Verificar registro oficial",
    "home.cardVerifyBody": "Los registros de reclusos siempre provienen directamente de la fuente gubernamental: el localizador del BOP o el localizador estatal.",
    "home.coverageTitle": "Jurisdicciones cubiertas",
    "home.coverageBody": "Fuentes oficiales gubernamentales priorizadas. Los administradores pueden añadir más estados.",
    "home.officialAvailable": "Localizador oficial disponible",
    "home.officialUnavailable": "Fuente oficial en línea no disponible actualmente",
    "home.open": "Abrir",
    "stats.facilities": "Instituciones monitoreadas",
    "stats.federal": "Instituciones federales (BOP)",
    "stats.jurisdictions": "Localizadores oficiales enlazados",
    "stats.verified": "Con estado verificado",
    "stats.flagged": "Con alerta activa",
    "flagged.title": "Instituciones con alertas recientes",
    "flagged.seeAll": "Todas las alertas para viajeros →",
    "flagged.body": "Encierros, visitas suspendidas o limitadas e incidentes — los más recientes primero.",
    "flagged.updated": "Actualizado",
    "flagged.empty": "Ninguna institución tiene actualmente un encierro o restricción de visitas.",

    "common.backSearch": "← Volver a la búsqueda",
    "common.newSearch": "← Nueva búsqueda",
    "common.loading": "Cargando...",

    "results.title": "Resultados de la búsqueda",
    "results.searching": "Buscando registros oficiales...",
    "results.matches_one": "posible coincidencia encontrada",
    "results.matches_other": "posibles coincidencias encontradas",
    "results.none": "No se encontraron registros coincidentes en el conjunto de datos de esta aplicación.",
    "results.noneHelp": "Pruebe ampliar su búsqueda o consulte directamente el localizador oficial del estado o del sistema federal.",
    "results.refine": "← Refinar búsqueda",
    "results.inmateId": "ID de recluso",
    "results.dob": "Fecha de nacimiento",
    "results.custody": "Estado de custodia",
    "results.system": "Sistema",
    "results.projectedRelease": "Fecha prevista de salida",
    "results.notPublic": "No disponible públicamente",
    "results.parole": "Libertad condicional",
    "results.demo": "Registro de demostración",
    "results.ciAddress": "Dirección de la institución correccional",
    "results.viewFacility": "Ver institución y visitas",
    "results.verifyOfficial": "Verificar registro oficial",
    "results.source": "Fuente",
    "results.lastChecked": "Última verificación",

    "facility.beforeYouTravel": "ANTES DE VIAJAR",
    "facility.beforeTravelLine1": "Las condiciones de las instituciones correccionales pueden cambiar sin previo aviso.",
    "facility.beforeTravelLine2": "Confirme la visita directamente con la institución antes de realizar un viaje largo.",
    "facility.callFacility": "Llamar",
    "facility.viewOfficialNotice": "Ver aviso oficial",
    "facility.directions": "Cómo llegar",
    "facility.institutionInfo": "Información de la institución",
    "facility.phone": "Teléfono",
    "facility.systemType": "Tipo de sistema",
    "facility.facilityType": "Tipo de institución",
    "facility.officialWebsite": "Sitio web oficial",
    "facility.visitationPage": "Página de visitas",
    "facility.currentStatus": "Estado actual",
    "facility.lastUpdated": "Última actualización",
    "facility.statusSource": "Fuente",
    "facility.officialSource": "Ver fuente oficial",
    "facility.visitationStatus": "Estado de visitas",
    "facility.visitationHeader": "Estado de visitas",
    "facility.visitationDays": "Días de visita",
    "facility.visitationHours": "Horario de visita",
    "facility.visitationPhone": "Teléfono para visitas",
    "facility.idRequirements": "Requisitos de identificación",
    "facility.visitationRules": "Reglas de visita",
    "facility.criticalAnnouncement": "Consulte el aviso oficial de la institución antes de viajar.",
    "facility.contactId": "Se requiere identificación oficial con foto. Verifique las reglas específicas con la institución.",
    "facility.verifyDirectly": "Verificar directamente",
    "facility.verifyDirectlyBody": "Antes de viajar, confirme el estado actual de esta institución directamente con ella. Los enlaces de teléfono y sitio oficial se muestran arriba.",
    "facility.openOfficialSource": "Abrir fuente oficial →",
    "facility.downloadPacket": "Descargar paquete del visitante (PDF)",

    "subscribe.title": "Recibir alertas de estado",
    "subscribe.subtitle": "Le notificaremos cuando cambie el estado de esta institución.",
    "subscribe.emailPlaceholder": "tu@correo.com",
    "subscribe.phonePlaceholder": "+1 (opcional, para SMS)",
    "subscribe.cta": "Avísame",
    "human.question": "¿Cuánto es {a} + {b}?",
    "human.loading": "Cargando verificación...",
    "human.placeholder": "Su respuesta",
    "human.refresh": "Nueva pregunta",
    "human.failed": "La verificación humana falló. Responda la pregunta de nuevo.",
    "subscribe.success": "Está suscrito. Revise su correo para los cambios de estado.",
    "subscribe.privacy": "Solo alertas transaccionales. Nunca compartimos su correo ni pedimos contraseñas por correo.",
    "subscribe.smsOff": "Las alertas por SMS estarán disponibles al añadir un teléfono (puede estar inactivo hasta que el administrador configure Twilio).",

    "map.title": "Mapa nacional de estado de instituciones",
    "map.subtitle": "Cada institución rastreada, con color según su estado actual. Haga clic en un marcador para ver detalles.",
    "map.legend": "Leyenda",

    "footer.notice": "Aviso de información pública",
    "footer.noticeBody": "Este portal reúne registros públicos de la Oficina Federal de Prisiones y de los Departamentos Estatales de Correcciones. Las condiciones de las instituciones pueden cambiar sin aviso.",
    "footer.noticeStrong": "Confirme la visita directamente con la institución antes de realizar un viaje largo.",
    "footer.data": "Política de datos",
    "footer.dataBody": "Las direcciones mostradas son solo direcciones de instituciones correccionales, no direcciones personales. Todos los resultados incluyen un enlace al registro oficial gubernamental.",
    "footer.emergency": "Emergencia",
    "footer.emergencyBody": "Si tiene una emergencia que involucra a una persona encarcelada, contacte directamente a la institución con el teléfono oficial listado en el perfil.",
    "footer.copyright": "Portal de estado de prisioneros e instituciones de EE. UU. · Herramienta no afiliada de información pública",

    "status.NORMAL": "OPERACIONES NORMALES",
    "status.RESTRICTED": "OPERACIONES RESTRINGIDAS",
    "status.LIMITED_VISITATION": "VISITAS LIMITADAS",
    "status.LOCKDOWN": "ENCIERRO",
    "status.VISITATION_SUSPENDED": "VISITAS SUSPENDIDAS",
    "status.EMERGENCY": "EMERGENCIA / INCIDENTE",
    "status.UNKNOWN": "Estado pendiente de verificación",
    "status.unverified": "Estado pendiente de verificación",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("pf_lang") || "en");

  useEffect(() => {
    localStorage.setItem("pf_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key, fallback) => TRANSLATIONS[lang][key] ?? TRANSLATIONS.en[key] ?? fallback ?? key;
  const toggle = () => setLang((l) => (l === "en" ? "es" : "en"));

  return <I18nContext.Provider value={{ lang, setLang, toggle, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
